import { Link } from "react-router-dom";
import {
  Eye,
  Bell,
  BarChart3,
  ArrowRight,
  DollarSign,
  Tag,
  Package,
  Star,
  Megaphone,
  PieChart,
} from "lucide-react";

const CompetitorMonitoring = () => {
  return (
    <div className="min-h-screen">
      <section className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-5xl font-bold mb-6">
              <span className="gradient-text">Competitor</span>
              <br />
              Monitoring
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Stay ahead of the competition with comprehensive monitoring of
              competitor pricing, promotions, and market strategies.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="/images/competitor-monitoring.png"
                alt="Competitor Analysis Dashboard"
                className="w-full h-auto rounded-2xl shadow-xl"
              />
            </div>
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 flex-shrink-0 border border-pink-600/70 rounded-lg flex items-center justify-center bg-white">
                  <Eye className="h-6 w-6 text-pink-600/90" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">
                    24/7 Monitoring
                  </h3>
                  <p className="text-gray-600">
                    Continuous tracking of competitor prices, promotions, and
                    product changes.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 flex-shrink-0 border border-pink-600/70 rounded-lg flex items-center justify-center bg-white">
                  <Bell className="h-6 w-6 text-pink-600/90" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Instant Alerts</h3>
                  <p className="text-gray-600">
                    Get notified immediately when competitors change prices or
                    launch promotions.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 flex-shrink-0 border border-pink-600/70 rounded-lg flex items-center justify-center bg-white">
                  <BarChart3 className="h-6 w-6 text-pink-600/90" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">
                    Market Intelligence
                  </h3>
                  <p className="text-gray-600">
                    Comprehensive analytics on competitor strategies and market
                    trends.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">
              <span className="gradient-text">Monitoring Features</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Price Tracking",
                description:
                  "Monitor competitor pricing across all channels and platforms",
                icon: DollarSign,
              },
              {
                title: "Promotion Detection",
                description:
                  "Identify competitor sales, discounts, and special offers",
                icon: Tag,
              },
              {
                title: "Product Changes",
                description: "Track new product launches and inventory updates",
                icon: Package,
              },
              {
                title: "Review Monitoring",
                description: "Monitor competitor ratings and customer feedback",
                icon: Star,
              },
              {
                title: "Ad Intelligence",
                description:
                  "Track competitor advertising campaigns and strategies",
                icon: Megaphone,
              },
              {
                title: "Market Share Analysis",
                description: "Understand your position relative to competitors",
                icon: PieChart,
              },
            ].map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <div key={index} className="card">
                  <div className="mb-4">
                    <IconComponent className="h-8 w-8 text-pink-600" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 gradient-bg">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Stay Ahead of Competition
          </h2>
          <p className="text-xl text-orange-100 mb-8">
            Monitor competitors 24/7 and react instantly to market changes
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/trial"
              className="bg-white text-gray-900 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-flex items-center justify-center"
            >
              Start Free Trial <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <Link
              to="/contact"
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-gray-900 transition-colors"
            >
              Schedule Demo
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CompetitorMonitoring;
